/**
 * Public-register lookups that pre-fill onboarding.
 *
 * A lookup fills FORM FIELDS the user then reviews and submits — it never answers a compliance question
 * on their behalf behind the scenes. Everything the register could not establish comes back null, and the
 * wizard falls back to asking. See COMPLIANCE-MODEL.md §6.
 */

import { apiGet } from '../apiClient';
import type { CompanyRegion, CompanyType } from './companies';

/**
 * Why a lookup did or did not produce a profile. `notFound` (the register has no such company) and
 * `unavailable` (we could not ask) are deliberately distinct: an unreachable register must never read as
 * "this company does not exist".
 */
export type RegisterLookupOutcome =
  | 'found'
  | 'invalidNumber'
  | 'notFound'
  | 'unsupportedJurisdiction'
  | 'unavailable'
  | 'notConfigured';

/**
 * A company as Companies House describes it. Every field may be null, meaning the register did not say —
 * never a default. A null `companyType` means "you still have to choose", NOT "other".
 */
export interface RegisterProfile {
  companyNumber: string;
  name: string | null;
  companyType: CompanyType | null;
  region: CompanyRegion | null;
  /** Self-declared SIC 2007 codes. EMPTY is common and is not evidence of anything. */
  sicCodes: string[];
  /** False for a dissolved or liquidated company. */
  isActive: boolean;
  registeredOfficePostcode: string | null;
  /** Coarse size signal: "micro-entity", "small", "full", "group". */
  lastAccountsType: string | null;
}

export interface RegisterLookup {
  outcome: RegisterLookupOutcome;
  companyNumber: string | null;
  profile: RegisterProfile | null;
  /**
   * What the FCA register said: 'authorised', 'notAuthorised', or null for "could not establish".
   * Null and 'notAuthorised' are NOT the same — the FCA returns an identical 404 for a bad key and an
   * unauthorised firm, so only a canary-verified miss reports notAuthorised.
   */
  fcaStatus: 'authorised' | 'notAuthorised' | null;
  /** FCA firm reference number when authorised. */
  fcaFirmReference: string | null;
}

/** GET /api/registers/companies-house/{number} — never throws for an expected outcome. */
export function lookupCompanyNumber(number: string): Promise<RegisterLookup> {
  return apiGet<RegisterLookup>(`/api/registers/companies-house/${encodeURIComponent(number)}`);
}

/**
 * What to tell the user when a lookup did not produce a profile. Every message says what happens next,
 * because in every case the answer is the same: carry on and fill it in by hand.
 */
export function lookupMessage(outcome: RegisterLookupOutcome): string | null {
  switch (outcome) {
    case 'found':
      return null;
    case 'invalidNumber':
      return 'That doesn’t look like a company number. It’s 8 characters, like 00445790 or SC016876.';
    case 'notFound':
      return 'No company with that number on Companies House. Check it, or carry on and set things up by hand.';
    case 'unsupportedJurisdiction':
      return 'That’s a Northern Ireland company. Reeve covers England, Wales and Scotland, so some rules here won’t match your situation.';
    case 'unavailable':
      return 'Companies House isn’t responding right now. Carry on and set things up by hand.';
    case 'notConfigured':
      return null; // The feature is simply off; no need to explain infrastructure to a user.
    default:
      return null;
  }
}
